import React, { useCallback, useMemo, useRef } from 'react';
import { set, get, cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useProductCostingState } from '../ProductCostingContext';
import { checkIsNumber, checkCurrency, roundOff, restrictToTwoDecimals } from '../../../../Components/helper';
import { testingFieldpath, errorpath } from '../fieldPath';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import BombiixAGGrid from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDefOrGroup, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import type { TestingRow, TestingState } from '../types';
import { UploadIcon } from '../../../../bombiix-icon';

/** Import-all button glyph, sized to the compact button it sits in. */
const ICON_SIZE_IMPORT = 18;

/** One entry of the testing library, both as offered by the typeahead and as stored on a row. */
interface TestingLibraryOption {
  _id?: string;
  code?: string;
  description?: string;
  type?: { name?: string };
  colour?: unknown;
  testPrice?: unknown;
  bomComponent?: { referenceCode?: string };
}

/** A colourway as stored on a testing row — the whole option object, not just its id. */
interface TestingColourway {
  name?: string;
  colourRef?: string;
  newReferences?: string;
}

/** A colour reference as stored on a testing row. */
interface TestingColourRef {
  name?: string;
  newReferences?: string;
}

/** The validation messages `globalError.testing` carries, one entry per testing row. */
interface TestingRowError {
  colourway?: string;
  totalPrice?: string;
  pricePerUnit?: string;
}

/** The redux slices this table reads. */
interface TestingRootState {
  newProductCostingData: { productCostingPreviousData?: Record<string, unknown> };
  UserDetails: { isSupplierLoggedIn?: boolean };
  SupplierSuggestion: { TestingLibraries?: TestingLibraryOption[] };
  BOMConfigDetails: { isProductArchived?: boolean };
}

/** The `clear()` slice of the react-bootstrap-typeahead instance API this table drives. */
interface TypeaheadInstance {
  clear: () => void;
}

/**
 * One rendered row: a testing row plus the grid's id and its validation messages.
 *
 * Declared with `type` rather than `interface` on purpose: BombiixAGGrid constrains its row
 * generic to `Record<string, unknown> & {…}`. Intersecting with `TestingRow` also narrows the
 * four fields the columns read off — `TestingRow` declares them `unknown`, and `unknown & T` is
 * `T` — so the accessors need no per-call cast.
 */
type TestingGridRow = TestingRow & {
  __cagId: string;
  __err: TestingRowError;
  testingCode?: TestingLibraryOption;
  colourway?: TestingColourway | null;
  colourRef?: TestingColourRef;
  isChangedBySupplierTotalPrice?: boolean;
  isChangedBySupplierPricePerUnit?: boolean;
};

export function TestingTable(): React.JSX.Element {
  const { productCostingstate, globalError, initialOptionsState, testingState } = useProductCostingState();
  const { t } = useTranslation('common');
  const typeaheadRef = useRef<TypeaheadInstance | null>(null);
  const { calculateTotPrice, calculatePricePerUnit, calculateTotalPrice } = useDetailedPageHook();

  const testingStateRef = useRef<TestingState>(testingState);
  testingStateRef.current = testingState;

  const {
    productInfo: { currency },
    isGlobalUrl,
  } = productCostingstate;

  const {
    testing: { testingInfo, sumOfPrice },
  } = testingState;

  const testingErrors = globalError.testing as TestingRowError[] | undefined;
  const { colourwayOptionsArr } = initialOptionsState;
  const {
    newProductCostingData: { productCostingPreviousData },
    UserDetails: { isSupplierLoggedIn },
    SupplierSuggestion: { TestingLibraries },
    BOMConfigDetails: { isProductArchived },
  } = useSelector((state: TestingRootState) => state);

  const { onChangeCheckIfErrorExist } = useValidationHooks();
  const { statusState, controlAmberColour, isDisabled, isSupplierView, markAsCompleteState, hideColourCol, onLoadDisable } = useShowHideSectionHooks();

  const rowData = useMemo<TestingGridRow[]>(
    () =>
      (testingInfo || []).map((row, i) => {
        const testingRow = row as TestingGridRow;
        return {
          ...testingRow,
          pricePerUnit:
            testingRow?.pricePerUnit === '' || testingRow?.pricePerUnit == null ? testingRow?.pricePerUnit : roundOff(Number(testingRow.pricePerUnit), 2),
          __cagId: testingRow?.testingCode?._id || `idx_${i}`,
          __err: testingErrors?.[i] || {},
        };
      }),
    [testingInfo, testingErrors],
  );

  const indexFromRowId = useCallback((rowId: BombiixRowId) => rowData.findIndex((row) => row.__cagId === rowId), [rowData]);

  const handleInputChange = useCallback(
    async (value: unknown, path: string, supplierPath: string | undefined, index: number, pricePerPath: string): Promise<void> => {
      const copyState: TestingState = cloneDeep(testingStateRef.current);

      set(copyState, path, value);
      if (supplierPath) {
        if (isSupplierLoggedIn || isGlobalUrl) {
          const previousValue = get(productCostingPreviousData, path);
          set(copyState, supplierPath, previousValue !== value);
        } else {
          set(copyState, supplierPath, false);
        }
      }
      await calculateTotPrice(copyState, 'sumOfPrice');
      const totalForPpu = copyState?.testing?.testingInfo?.[index]?.totalPrice;
      await calculatePricePerUnit(copyState, totalForPpu, index, pricePerPath);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSupplierLoggedIn, isGlobalUrl, productCostingPreviousData, calculateTotPrice, calculatePricePerUnit, productCostingstate],
  );

  const handleInputChangeTestPrice = useCallback(
    async (value: unknown, path: string, supplierPath: string | undefined, index: number, testPricePath: string): Promise<void> => {
      const copyState: TestingState = cloneDeep(testingStateRef.current);
      set(copyState, path, value);
      if (supplierPath) {
        if (isSupplierLoggedIn || isGlobalUrl) {
          const previousValue = get(cloneDeep(productCostingPreviousData), path);
          set(copyState, supplierPath, previousValue !== value);
        } else {
          set(copyState, supplierPath, false);
        }
      }
      const ppu = copyState?.testing?.testingInfo?.[index]?.pricePerUnit;
      await calculateTotalPrice(copyState, ppu, index, testPricePath);
      await calculateTotPrice(copyState, 'sumOfPrice');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSupplierLoggedIn, isGlobalUrl, productCostingPreviousData, calculateTotPrice, calculateTotalPrice, productCostingstate],
  );

  const updateError = useCallback((path: string): void => onChangeCheckIfErrorExist(path, globalError), [onChangeCheckIfErrorExist, globalError]);

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const index = indexFromRowId(rowId);
      if (index < 0) return;
      switch (field) {
        case 'colourway':
          handleInputChange(value, testingFieldpath(index).SELECTED_COLOURWAY, undefined, index, testingFieldpath(index).PRICE_PER_UNIT);
          if (get(testingErrors, `[${index}].colourway`, '')) updateError(errorpath(index).TESTING.ERROR_COLOURWAY);
          break;

        case 'totalPrice': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          handleInputChange(
            restricted,
            testingFieldpath(index).TOTAL_PRICE,
            testingFieldpath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE,
            index,
            testingFieldpath(index).PRICE_PER_UNIT,
          );
          if (get(testingErrors, `[${index}].totalPrice`, '')) updateError(errorpath(index).TESTING.ERROR_TOTPRICE);
          break;
        }

        case 'pricePerUnit': {
          const restricted = restrictToTwoDecimals(value);
          if (!checkIsNumber(restricted || 0)) return;
          handleInputChangeTestPrice(
            restricted,
            testingFieldpath(index).PRICE_PER_UNIT,
            testingFieldpath(index).IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT,
            index,
            testingFieldpath(index).TOTAL_PRICE,
          );
          if (get(testingErrors, `[${index}].pricePerUnit`, '')) updateError(errorpath(index).TESTING.ERROR_PRICEPERPRICE);
          break;
        }

        case 'isExcluded':
          handleInputChange(value, testingFieldpath(index).IS_EXCLUDED, undefined, index, testingFieldpath(index).PRICE_PER_UNIT);
          break;

        default:
          break;
      }
    },
    [indexFromRowId, handleInputChange, handleInputChangeTestPrice, testingErrors, updateError],
  );

  const handleFillCells = useCallback(
    async (changes: BombiixFillBatchItem<TestingGridRow>[]): Promise<void> => {
      if (!Array.isArray(changes) || changes.length === 0) return;
      const copyState: TestingState = cloneDeep(testingStateRef.current);
      const supplierActive = isSupplierLoggedIn || isGlobalUrl;
      const filled: Array<{ index: number; field: string }> = [];

      changes.forEach((change) => {
        const index = indexFromRowId(change.rowId);
        if (index < 0) return;
        const fp = testingFieldpath(index);

        switch (change.field) {
          case 'colourway':
            set(copyState, fp.SELECTED_COLOURWAY, change.newValue);
            filled.push({ index, field: 'colourway' });
            break;
          case 'totalPrice': {
            const restricted = restrictToTwoDecimals(change.newValue);
            if (!checkIsNumber(restricted || 0)) return;
            set(copyState, fp.TOTAL_PRICE, restricted);
            set(copyState, fp.IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE, supplierActive ? get(productCostingPreviousData, fp.TOTAL_PRICE) !== restricted : false);
            filled.push({ index, field: 'totalPrice' });
            break;
          }
          case 'pricePerUnit': {
            const restricted = restrictToTwoDecimals(change.newValue);
            if (!checkIsNumber(restricted || 0)) return;
            set(copyState, fp.PRICE_PER_UNIT, restricted);
            set(
              copyState,
              fp.IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT,
              supplierActive ? get(productCostingPreviousData, fp.PRICE_PER_UNIT) !== restricted : false,
            );
            filled.push({ index, field: 'pricePerUnit' });
            break;
          }
          case 'isExcluded':
            set(copyState, fp.IS_EXCLUDED, change.newValue);
            filled.push({ index, field: 'isExcluded' });
            break;
          default:
            break;
        }
      });

      for (const { index, field } of filled) {
        if (field === 'pricePerUnit') {
          const ppu = get(copyState, `testing.testingInfo[${index}].pricePerUnit`);
          await calculateTotalPrice(copyState, ppu, index, testingFieldpath(index).TOTAL_PRICE);
        } else {
          const total = get(copyState, `testing.testingInfo[${index}].totalPrice`);
          await calculatePricePerUnit(copyState, total, index, testingFieldpath(index).PRICE_PER_UNIT);
        }
      }
      await calculateTotPrice(copyState, 'sumOfPrice');
    },
    [indexFromRowId, isSupplierLoggedIn, isGlobalUrl, productCostingPreviousData, calculateTotalPrice, calculatePricePerUnit, calculateTotPrice],
  );

  const columnDefs = useMemo<BombiixColumnDefOrGroup<TestingGridRow>[]>(() => {
    const cols: BombiixColumnDefOrGroup<TestingGridRow>[] = [];

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.COLOUR_WAY'),
        required: true,
        cellType: 'select',
        width: 150,
        sortable: false,
        getCellReadOnly: () => isDisabled,
        getCellError: (row: TestingGridRow) => row.__err.colourway,
        cellProps: {
          options: colourwayOptionsArr,
          getOptionLabel: (opt: TestingColourway) => opt?.name,
          getOptionValue: (opt: TestingColourway) => opt?.name,
          accessor: (row: TestingGridRow) => row?.colourway?.name,
        },
      });
    }

    cols.push({
      field: 'testingCode',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.TESTING_CODE'),
      cellType: 'readonlyText',
      width: 140,
      sortable: false,
      cellProps: { accessor: (row: TestingGridRow) => row?.testingCode?.code },
    });

    cols.push({
      field: 'testingDesc',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.TESTING_DESCRIPTION'),
      cellType: 'readonlyText',
      minWidth: 517,
      flex: 1,
      sortable: false,
      cellProps: { accessor: (row: TestingGridRow) => row?.testingCode?.description },
    });

    cols.push({
      field: 'testingType',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.TESTING_TYPE'),
      cellType: 'readonlyText',
      width: 140,
      sortable: false,
      cellProps: { accessor: (row: TestingGridRow) => row?.testingCode?.type?.name },
    });

    cols.push({
      field: 'bomComponent',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.BOM_COMPONENT'),
      cellType: 'readonlyText',
      width: 155,
      sortable: false,
      cellProps: { accessor: (row: TestingGridRow) => row?.testingCode?.bomComponent?.referenceCode },
    });

    cols.push({
      field: 'colourRef',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.COLOUR_REF'),
      cellType: 'readonlyText',
      width: 140,
      sortable: false,
      cellProps: {
        accessor: (row: TestingGridRow) =>
          row?.colourRef?.name || row?.colourRef?.newReferences || row?.colourway?.colourRef || row?.colourway?.newReferences || '--',
      },
    });

    cols.push({
      field: 'totalPrice',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.TEST_PRICE'),
      cellType: 'currency',
      width: 107,
      sortable: false,
      getCellReadOnly: () => statusState,
      getCellError: (row: TestingGridRow) => row.__err.totalPrice,
      getCellClass: (row: TestingGridRow) => (row.isChangedBySupplierTotalPrice && controlAmberColour ? 'suggested-data' : ''),
      footerCellProps: {
        prefixLabel: t('DEVLOPMENT_ORDER.TESTING_TABLE.TOTAL', { ns: 'costing' }),
        format: (value: unknown) => `${checkCurrency(currency, value)}${roundOff(value, 2, true)}`,
      },
    });

    cols.push({
      field: 'pricePerUnit',
      headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.PRICE_PER_UNIT'),
      cellType: 'currency',
      width: 129,
      sortable: false,
      getCellReadOnly: () => statusState,
      getCellError: (row: TestingGridRow) => row.__err.pricePerUnit,
      getCellClass: (row: TestingGridRow) => (row.isChangedBySupplierTotalPrice && controlAmberColour ? 'suggested-data' : ''),
    });

    if (!isSupplierView) {
      cols.push({
        field: 'isExcluded',
        headerName: t('DEVLOPMENT_ORDER.TESTING_TABLE.EXCLUDE'),
        cellType: 'checkbox',
        width: 72,
        sortable: false,
        cellClass: 'cag-cell-center',
        headerClass: 'cag-header-center',
        getCellDisabled: () => Boolean(markAsCompleteState || isProductArchived),
        getCellClass: (row: TestingGridRow) => (row.isChangedBySupplierPricePerUnit && controlAmberColour ? 'suggested-data' : ''),
      });
    }

    return cols;
  }, [
    hideColourCol,
    isDisabled,
    statusState,
    controlAmberColour,
    currency,
    markAsCompleteState,
    isProductArchived,
    isSupplierView,
    colourwayOptionsArr,
    t,
  ]);

  /* The pinned footer carries the total alone. BombiixAGGrid short-circuits every per-row
     accessor on a `__cagFooter` row, so the row-shaped fields are deliberately absent — exactly
     as in the JavaScript original.

     Passed to the grid as `?? []` rather than by conditional spread: BombiixAGGrid only forwards
     `pinnedBottomRowData` when it is truthy, so omitting the prop leaves a previously-set footer
     in place. The row list emptying (a costing-type change) would then strand a stale total on an
     empty grid. An empty array survives that guard and clears the footer, matching the original,
     which always passes the prop and lets `undefined` clear it. */
  const pinnedBottomRowData = useMemo<TestingGridRow[] | undefined>(
    () => (testingInfo?.length > 1 ? [{ __cagFooter: true, totalPrice: sumOfPrice } as unknown as TestingGridRow] : undefined),
    [testingInfo?.length, sumOfPrice],
  );

  const testingCodeOptions = useMemo<TestingLibraryOption[]>(() => TestingLibraries || [], [TestingLibraries]);

  const handTestCodeSelect = async (selected: TestingLibraryOption[], isAll?: boolean): Promise<void> => {
    /* When `isAll` is set the whole array is imported and `test` is never read; the cast keeps the
       single-select path — the only one that reads it — typed. */
    const test = (isAll ? selected : selected?.[0]) as TestingLibraryOption;
    const cloneTestingState: TestingState = cloneDeep(testingState);

    if (!isAll) {
      const testId = test?._id;
      const existingTest = testingState?.testing?.testingInfo?.find((item) => (item?.testingCode as TestingLibraryOption | undefined)?._id === testId);
      if (existingTest) {
        toasterMsg('Testing code  already exists', 'danger');
        if (typeaheadRef?.current) typeaheadRef.current.clear();
        return;
      }
    }

    const makeObj = (tst: TestingLibraryOption): TestingRow => ({
      colourway: null,
      colourRef: tst?.colour,
      testingCode: tst,
      testingDesc: tst.description,
      testingType: tst.type,
      totalPrice: tst?.testPrice || '',
      isExcluded: false,
      isChangedBySupplierTotalPrice: false,
      isChangedBySupplierPricePerUnit: false,
      pricePerUnit: '',
      bomComponent: tst?.bomComponent?.referenceCode,
    });

    if (isAll) {
      cloneTestingState.testing.testingInfo = [];
      selected.forEach((tst) => cloneTestingState.testing.testingInfo.push(makeObj(tst)));
    } else {
      cloneTestingState.testing.testingInfo.push(makeObj(test));
    }

    set(cloneTestingState, testingFieldpath().TESTING_INFO_DATA, cloneTestingState?.testing?.testingInfo);
    await calculateTotPrice(cloneTestingState, 'sumOfPrice');
    await calculatePricePerUnit(cloneTestingState);

    if (typeaheadRef?.current) typeaheadRef.current.clear();
  };
  return (
    <div className="costing-card p-12px">
      <div className="d-flex align-items-center mb-8">
        <p className="product-cp-title mb-0 mr-8"> {t('DEVLOPMENT_ORDER.COSTING_DETAILS.TESTING')}</p>
        {!isSupplierView && (
          <>
            <div className="testing-typeahead">
              <Typeahead
                id="object-options"
                labelKey="code"
                options={testingCodeOptions}
                onChange={handTestCodeSelect}
                multiple={false}
                minLength={1}
                placeholder={t('DEVLOPMENT_ORDER.TESTING_TABLE.SEARCH_IMPORT_TESTING', { ns: 'costing' })}
                ref={typeaheadRef}
                disabled={onLoadDisable || statusState}
              />
            </div>
            <CustomTooltip tooltipText={t('DEVLOPMENT_ORDER.PRODUCT_COSTING.ALL_IMPORT')}>
              <button
                disabled={onLoadDisable || isSupplierView || statusState}
                type="button"
                className="bombiix-icon-container bombiix-icon-container--blue ml-8"
                onClick={() => handTestCodeSelect(testingCodeOptions, true)}
                aria-label="button"
              >
                <UploadIcon size={ICON_SIZE_IMPORT} />
              </button>
            </CustomTooltip>
          </>
        )}
      </div>
      <BombiixAGGrid<TestingGridRow>
        fullWidthHorizontalScroll
        gridOptions={{ suppressMovableColumns: false }}
        hideCellErrorMessage
        revealHeaderControlsOnHover
        sortable={false}
        showColumnMenu={false}
        enableFillHandle
        onFillCells={handleFillCells}
        columnDefs={columnDefs}
        rowData={rowData}
        getRowId={(row) => row.__cagId}
        onRowChange={onRowChange}
        currencySymbol={checkCurrency(currency, undefined, true)}
        pinnedBottomRowData={pinnedBottomRowData ?? []}
      />
    </div>
  );
}

export default TestingTable;
